// Copyright ©2026 Hitachi High-Tech Corporation All Rights Reserved.
import { Injectable, NgZone } from '@angular/core';
import { WasmRuntimeBaseService, WasmHandle } from '../runtime/wasm-runtime-base.service';
import { applyGlobals, GlobalMap } from '../utils/apply-globals.util';

@Injectable({ providedIn: 'root' })
export class AlignmentViewWasmFacade {
  // ===== Fields =====
  private started = false;
  private handle: WasmHandle | null = null;
  private restoreGlobals: (() => void) | null = null;
  private restoreCallbacks: (() => void) | null = null;

  // ===== Constructor =====
  constructor(
    private base: WasmRuntimeBaseService,
    private zone: NgZone,
  ) {}

  // ===== Public API =====
  public async prepare(globals?: GlobalMap) {
    if (this.started) return;

    if (globals && Object.keys(globals).length > 0) {
      this.restoreGlobals = applyGlobals(globals);
    }

    try {
      this.handle = await this.base.createAndRun('wasm/alignmentview.wasm');
      this.started = true;
    } catch (e) {
      this.restoreGlobals?.(); this.restoreGlobals = null;
      this.handle = null;
      this.started = false;
      throw e;
    }
  }

  public async waitForExports(
    names: string[] = ['dataInit', 'readSVjson'],
    timeoutMs = 10000
  ): Promise<void> {
    this.ensureReady();
    const start = Date.now();
    await this.zone.runOutsideAngular(() =>
      new Promise<void>((resolve, reject) => {
        const t = setInterval(() => {
          const ready = names.every(n => typeof (globalThis as any)[n] === 'function');
          if (ready) {
            clearInterval(t);
            resolve();
          } else if (Date.now() - start > timeoutMs) {
            clearInterval(t);
            reject(new Error(`Timed out waiting for WASM exports: ${names.join(', ')}`));
          }
        }, 30);
      })
    );
  }

  public registerCallback(handlers: {
    getAssembleData?: (contigId: number) => Promise<string>;
    openUCSC?: (chr: string, pos: number) => void;
    unblock?: () => void;
  }) {
    const prevDescMap = new Map<string, PropertyDescriptor | undefined>();
    const hadOwnMap = new Map<string, boolean>();

    const define = (name: string, fn: Function | undefined) => {
      if (!fn) return;
      const hasOwn = Object.prototype.hasOwnProperty.call(globalThis, name);
      hadOwnMap.set(name, hasOwn);
      prevDescMap.set(name, hasOwn ? Object.getOwnPropertyDescriptor(globalThis, name) : undefined);
      Object.defineProperty(globalThis as any, name, {
        value: fn, configurable: true, enumerable: false, writable: true,
      });
    };

    define('get_assembleData', handlers.getAssembleData);
    define('openUCSC', handlers.openUCSC);
    define('unblock', handlers.unblock);

    this.restoreCallbacks = () => {
      for (const [name, desc] of prevDescMap.entries()) {
        const had = hadOwnMap.get(name);
        try {
          if (had && desc) Object.defineProperty(globalThis as any, name, desc);
          else delete (globalThis as any)[name];
        } catch {
          try { delete (globalThis as any)[name]; } catch {}
        }
      }
    };
  }

  public callDataInit(value: string) {
    this.callWasm('dataInit', value);
  }

  public callReadSVjson(sv: string, init: boolean) {
    this.callWasm('readSVjson', sv, init);
  }

  public callChangeHideSmall(value: number) {
    this.callWasm('changeHideSmall', value);
  }

  public callDraw(): void {
    const doc = globalThis.document;
    if (!doc) return;
    const el = doc.getElementById('smallAlignment');
    if (!(el instanceof HTMLInputElement)) return;
    const ev = new Event('change', { bubbles: true });
    el.dispatchEvent(ev);
  }

  public callEvent(type: string, payload: Partial<{
    offsetX: number;
    offsetY: number;
    deltaY: number;
    button: number;
  }> = {}): void {
    const doc = globalThis.document;
    if (!doc) return;
    const el = doc.getElementById('main_canvas');
    if (!(el instanceof HTMLCanvasElement)) return;
    const rect = el.getBoundingClientRect();
    const cx = (payload.offsetX ?? 0) + rect.left;
    const cy = (payload.offsetY ?? 0) + rect.top;
    switch (type) {
      case 'mousemove':
      case 'mousedown':
      case 'mouseup': {
        const evt = new MouseEvent(type, {
          bubbles: true,
          clientX: cx,
          clientY: cy,
          button: payload.button ?? 0,
        });
        el.dispatchEvent(evt);
        break;
      }
      case 'wheel':
      case 'mousewheel': {
        const anyWheel: any = { bubbles: true, deltaY: payload.deltaY ?? 0 };
        try {
          (anyWheel as any).wheelDelta = payload.deltaY ? -payload.deltaY : 0;
        } catch {}
        const evt1 = new WheelEvent('wheel', anyWheel as WheelEventInit);
        el.dispatchEvent(evt1);
        const evt2 = new WheelEvent('mousewheel', anyWheel as WheelEventInit);
        el.dispatchEvent(evt2);
        break;
      }
      default: {
        const evt = new Event(type, { bubbles: true });
        el.dispatchEvent(evt);
        break;
      }
    }
  }

  public async dispose(): Promise<void> {
    try {
      // Intentionally no-op:
      // - The Go/WASM module has no exported disposer.
      // - Releasing references and restoring globals enables GC to collect.
    } catch {
      // Swallow any unexpected errors during teardown.
    }

    this.restoreCallbacks?.(); this.restoreCallbacks = null;
    this.restoreGlobals?.(); this.restoreGlobals = null;

    this.handle = null;
    this.started = false;
  }

  // ===== Private Helpers =====
  private ensureReady() {
    if (!this.started || !this.handle) {
      throw new Error('WASM is not prepared. Call prepare() first.');
    }
    if (!this.handle.isRunning()) {
      throw new Error('WASM runtime is not running.');
    }
  }

  private callWasm(name: string, ...args: unknown[]): unknown {
    this.ensureReady();
    const fn = this.handle!.getGlobal<(...vs: unknown[]) => unknown>(name);
    if (!fn) throw new Error(`${name} is not defined by WASM`);
    return fn(...args);
  }
}